import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "../i18n";
import useSeo from "../hooks/useSeo";
import { Check, Crown, Zap, Users, Infinity as InfinityIcon } from "lucide-react";
import { PLAN_META, formatXof, api } from "../api";

const DURATIONS = ["1m", "3m", "6m", "12m"];
const TIERS = [
  { key: "single",    icon: Zap,          hue: "emerald" },
  { key: "multi",     icon: Users,        hue: "cyan" },
  { key: "unlimited", icon: InfinityIcon, hue: "amber" },
];

const hueClasses = {
  emerald: {
    ring: "border-emerald-400/50 glow-emerald",
    badge: "bg-emerald-400 text-[#05050A]",
    btn: "bg-emerald-400 hover:bg-emerald-300 text-[#05050A]",
    accent: "text-emerald-300",
  },
  cyan: {
    ring: "border-cyan-400/50 glow-cyan",
    badge: "bg-cyan-400 text-[#05050A]",
    btn: "bg-cyan-400 hover:bg-cyan-300 text-[#05050A]",
    accent: "text-cyan-300",
  },
  amber: {
    ring: "border-amber-400/50 glow-amber",
    badge: "bg-amber-400 text-[#05050A]",
    btn: "bg-amber-400 hover:bg-amber-300 text-[#05050A]",
    accent: "text-amber-300",
  },
};

export default function Pricing() {
  const { t } = useI18n();
  const { lang } = useI18n();
  useSeo({
    title: lang === "fr"
      ? "Tarifs — Bot de Trading MT5 Dr Trade Scalper Pro (à partir de 15 000 FCFA)"
      : "Pricing — Dr Trade Scalper Pro MT5 Trading Bot (from USD 25)",
    description: lang === "fr"
      ? "Tarifs du bot de trading Dr Trade Scalper Pro. 12 plans (Single 2 comptes, Multi 6 comptes, Illimité) × 4 durées (1/3/6/12 mois). Paiement USDT, Mobile Money, Carte Bancaire."
      : "Dr Trade Scalper Pro MT5 trading bot pricing. 12 plans (Single 2 accounts, Multi 6 accounts, Unlimited) × 4 durations (1/3/6/12 months). USDT, Mobile Money, Card payments.",
    keywords: lang === "fr"
      ? "prix EA MT5, tarif bot trading, acheter robot MT5, licence Expert Advisor, prix robot scalping"
      : "MT5 EA price, trading bot pricing, buy MT5 robot, Expert Advisor license, scalping robot cost",
    path: "/pricing",
  });
  const navigate = useNavigate();
  const [duration, setDuration] = useState("3m");
  const [eaVersion, setEaVersion] = useState("");
  useEffect(() => {
    let cancelled = false;
    api.get("/ea/version").then(({ data }) => {
      if (!cancelled && data?.version) setEaVersion(String(data.version));
    }).catch(() => { /* silencieux */ });
    return () => { cancelled = true; };
  }, []);

  const includes = t("pricing.includes_list") || [];

  const labelDuration = {
    "1m": t("pricing.duration_1m"),
    "3m": t("pricing.duration_3m"),
    "6m": t("pricing.duration_6m"),
    "12m": t("pricing.duration_12m"),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20" data-testid="page-pricing">
      <div className="text-center max-w-2xl mx-auto mb-12">
        <div className="section-overline mb-3">{t("pricing.overline")}</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter mb-4">{t("pricing.title")}</h1>
        {eaVersion && (
          <div
            className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-300 text-xs font-medium"
            data-testid="pricing-ea-version"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
            EA disponible : v{eaVersion}
          </div>
        )}
        <p className="text-zinc-400">{t("pricing.sub")}</p>
      </div>

      {/* Duration toggle */}
      <div className="flex justify-center mb-10">
        <div
          className="inline-flex bg-white/[0.03] border border-white/[0.08] rounded-full p-1 flex-wrap gap-1"
          data-testid="pricing-duration-toggle"
        >
          {DURATIONS.map((d) => (
            <button
              key={d}
              onClick={() => setDuration(d)}
              className={`px-4 sm:px-5 py-2 text-xs sm:text-sm rounded-full transition-all ${
                duration === d
                  ? "bg-cyan-400 text-[#05050A] font-semibold"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid={`pricing-duration-${d}`}
            >
              {labelDuration[d]}
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
        {TIERS.map((tier) => {
          const planId = `${tier.key}-${duration}`;
          const meta = PLAN_META[planId];
          if (!meta) return null;
          const hue = hueClasses[tier.hue];
          const Icon = tier.icon;

          return (
            <div
              key={tier.key}
              className={`relative glass rounded-3xl p-7 flex flex-col ${
                meta.popular ? hue.ring : "border-white/[0.08]"
              }`}
              data-testid={`pricing-card-${tier.key}`}
            >
              {meta.popular && (
                <div
                  className={`absolute -top-3 left-1/2 -translate-x-1/2 ${hue.badge} text-[10px] font-bold tracking-[0.2em] px-3 py-1 rounded-full inline-flex items-center gap-1`}
                >
                  <Crown className="h-3 w-3" />
                  {t("pricing.most_popular")}
                </div>
              )}

              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-white/[0.05] ${hue.accent}`}>
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-zinc-300 text-sm font-semibold">
                    {t(`pricing.${tier.key}`)}
                  </div>
                  <div className="text-[11px] text-zinc-500 uppercase tracking-wide">
                    {t(`pricing.${tier.key}_sub`)}
                  </div>
                </div>
              </div>

              <div className="flex items-baseline gap-1 mb-1">
                <div className="text-4xl font-bold tracking-tighter font-mono">
                  {formatXof(meta.xof)}
                </div>
                <div className="text-lg text-zinc-400 font-light">{t("common.currency")}</div>
              </div>
              <div className="text-xs text-zinc-500 mb-3">
                ≈ ${meta.usd.toLocaleString("en-US")} USD · {labelDuration[duration]}
              </div>
              {meta.saveLabel ? (
                <div className="text-xs text-emerald-400 mb-5 font-mono">
                  {t("pricing.save")} {meta.saveLabel}
                </div>
              ) : (
                <div className="mb-5 h-4" />
              )}

              <button
                onClick={() => navigate(`/checkout/${planId}`)}
                className={`w-full py-3 rounded-lg text-sm font-semibold transition-all ${
                  meta.popular ? hue.btn : "btn-ghost hover:border-white/30"
                }`}
                data-testid={`pricing-cta-${tier.key}`}
              >
                {t("pricing.cta")}
              </button>

              <div className="mt-6 pt-5 border-t border-white/[0.06] flex-1">
                <div className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-3">
                  {t("pricing.includes")}
                </div>
                <ul className="space-y-2">
                  {includes.map((line, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-300">
                      <Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />
                      <span>{line}</span>
                    </li>
                  ))}
                  <li className={`flex items-start gap-2 text-sm ${hue.accent} pt-2 border-t border-white/[0.04] mt-2`}>
                    <Check className="h-4 w-4 shrink-0 mt-0.5" />
                    <span className="font-medium">
                      {tier.key === "single" && t(`pricing.note_single_${duration}`) !== `pricing.note_single_${duration}`
                        ? t(`pricing.note_single_${duration}`)
                        : t(`pricing.note_${tier.key}`)}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-zinc-500 mt-10">
        {t("pricing.payment_methods")}
      </p>
    </div>
  );
}
